/* eslint-disable no-useless-escape */
import React, {
  useEffect,
  useState,
  useRef,
  useCallback,
  useMemo
} from "react";

import propTypes from "prop-types";
import classnames from "classnames";

import { createDataAttributes, isObject } from "../../utilities";
import { usePrevious } from "../../utilities/hooks";
import Label from "../form-inputs/Label";
import Icon from "../icons/Icon";

import { SelectMenu } from "../form-inputs/Select";

const SelectNonForm = ({
  className,
  dataAttrib,
  disabled,
  id,
  size,
  name,
  label,
  labelElement,
  value,
  placeholder,
  onChange,
  onBlur,
  onKeyDown,
  options,
  hideLabel,
  noLabel,
  fieldErrors,
  isClearable,
  showSearch,
  hideErrors,
  dataWrapperAttrib,
  optionListFullWidth,
  isLoading,
  loadingMessage,
  compactIndicator,
  ...rest
}) => {
  const mainRef = useRef(null);
  const menuRef = useRef(null);

  const [show, setShow] = useState(false);
  const [selectedOption, setSelectedOption] = useState();
  const [optionFilter, setOptionFilter] = useState("");

  const makeOptionKey = useCallback(
    ({ i, key, label, value }) => {
      const k = (i || key || label || "").toString().replace(/[W]/g, "_");
      const v = (value || "").toString().replace(/[W]/g, "_");
      return `${id}_${k}_${v}`;
    },
    [id]
  );

  /**
   * this allows a Select to accept options that are both
   * an array of objects [{value, label}]
   * or an array of values [value1, value2]
   */
  const opts = useMemo(
    () =>
      options.length
        ? isObject(options[0])
          ? options.map(o => ({
              ...o,
              key: makeOptionKey({
                label: (o.label || "").toString(),
                value: o.value,
                k: o.key,
                i: o.id
              })
            }))
          : options.map(o => ({
              label: (o || "").toString(),
              value: o,
              key: makeOptionKey({
                label: (o || "").toString(),
                value: o
              })
            }))
        : [],
    [makeOptionKey, options]
  );

  const handleSelectBlur = useCallback(() => {
    if (!show) {
      // setSelectFocused(false);
      if (onBlur) onBlur({ field: name, value });
    }
  }, [name, onBlur, show, value]);

  const handleSelectKeyPress = event => {
    const k = event.key;

    // if keydown includes CTRL or ALT key, then ignore
    if (event.metaKey) return false;

    if (k === "Tab") {
      return;
    }

    event.preventDefault();
    event.stopPropagation();

    if (onKeyDown) onKeyDown({ event, value });

    if (!show && k === "ArrowDown") {
      event.preventDefault();
      setShow(true);
      setTimeout(() => {
        const firstItem = menuRef?.current?.querySelector("button");
        if (firstItem) firstItem.focus();
      });
    } else if (show && k === "ArrowDown") {
      event.preventDefault();
      const firstItem = menuRef?.current?.querySelector("button");
      if (firstItem) firstItem.focus();
    } else if (show && k === "Escape") setShow(false);
    else if (k.length === 1) {
      setShow(true);
      setOptionFilter(k);
    }
  };

  const handleSelectKeyDown = event => {
    const k = event.key;
    if (k === "Tab") {
      setShow(false);
      return;
    }

    event.preventDefault();
    event.stopPropagation();
  };

  const focusMainElement = () => {
    // setSelectFocused(true);
    const select = mainRef?.current?.querySelector("button.uikit__select__btn");
    if (select) select.focus();
  };

  const handleClick = () => {
    focusMainElement();
    if (opts.length > 0) setShow(!show); // toggles options when user clicks on input multiple times
  };

  const handleOptionSelect = option => {
    setShow(false);
    setSelectedOption(option);
    onChange({
      field: name,
      label: name,
      value: option?.value || ""
    });
    focusMainElement();
  };

  const handleClear = () => {
    setSelectedOption(null);
    onChange({ field: name, value: "" });
    focusMainElement();
  };

  useEffect(() => {
    if (opts?.length && value !== selectedOption?.value) {
      setSelectedOption(opts.find(o => o.value === value));
    }
  }, [opts, value, selectedOption]);

  useEffect(() => {
    if (!show && !!optionFilter) setOptionFilter("");
  }, [optionFilter, show]);

  const wasShown = usePrevious(show);
  useEffect(() => {
    if (wasShown && !show) handleSelectBlur();
  }, [wasShown, show, handleSelectBlur]);

  const isDisabled = disabled || isLoading;

  const hasValue = !!value;

  const showClearButton = !isLoading && isClearable && hasValue;

  const getOpt = v =>
    (opts || []).find(
      o => o.value === v || (isObject(value) && o.value === v.value)
    ) || {};
  // -------------------------
  //     STYLES/CLAASES
  // -------------------------
  const sizeClass = size ? "uikit__select-size__" + size : null;

  const containerClasses = classnames(
    "uikit__form-item",
    "uikit__select-wrapper",
    className,
    sizeClass,
    {
      "uikit__hide-label": hideLabel,
      uikit__select__expanded: show,
      "uikit__select-disabled": isDisabled,
      "uikit__select-compact": compactIndicator
    }
  );

  const selectClass = classnames("uikit__select__btn", {
    "has-value": hasValue,
    "uikit__select__show-clear-button": showClearButton
  });

  return (
    <div
      className={containerClasses}
      {...createDataAttributes(dataWrapperAttrib)}
    >
      {!noLabel && (
        <Label htmlFor={id} label={label} labelElement={labelElement} />
      )}
      <div className={classnames("uikit__select", {})}>
        <div
          id={`${id}__wrapper`}
          ref={mainRef}
          className="uikit__select__btn-wrapper"
        >
          <button
            role="listbox"
            aria-expanded={show}
            autoComplete="off"
            data-1p-ignore
            type="button"
            id={id}
            name={name}
            className={selectClass}
            onBlur={handleSelectBlur}
            onFocus={focusMainElement}
            onClick={handleClick}
            onKeyUp={handleSelectKeyPress}
            onKeyDown={handleSelectKeyDown}
            disabled={isDisabled}
            {...createDataAttributes(dataAttrib)}
            {...rest}
          >
            {!isLoading && !hasValue && (
              <span className="uikit__select__placeholder">{placeholder}</span>
            )}
            {isLoading && (
              <span className="uikit__select__placeholder">
                {loadingMessage}
              </span>
            )}
            <span className="uikit__select__selected-label">
              {getOpt(value)?.label || ""}
            </span>
            <span aria-hidden="true" className="uikit__select__selected-value">
              {getOpt(value)?.value || ""}
            </span>
          </button>
          {showClearButton && (
            <button
              type="button"
              className="uikit__select__clear"
              onClick={handleClear}
            >
              <Icon icon="farTimes" />
            </button>
          )}
          <button
            className="uikit__select__indicator"
            tabIndex="-1"
            disabled={disabled}
            type="button"
            onClick={handleClick}
          >
            <Icon icon={show ? "fasAngleDown" : "farAngleDown"} />
          </button>
          {show && (
            <SelectMenu
              ref={menuRef}
              id={id}
              value={
                opts.length
                  ? getOpt(value).value
                  : value === undefined || isObject(value)
                  ? value
                  : { value, label: value }
              }
              groupedOptions
              options={opts}
              mainRef={mainRef}
              setShow={setShow}
              onSelect={handleOptionSelect}
              optionListFullWidth={optionListFullWidth}
              optionFilter={optionFilter}
              setOptionFilter={setOptionFilter}
              showSearch={showSearch}
            />
          )}
        </div>
      </div>
    </div>
  );
};

SelectNonForm.propTypes = {
  className: propTypes.string,
  disabled: propTypes.bool,

  id: propTypes.string.isRequired,
  label: propTypes.oneOfType([
    propTypes.string,
    propTypes.object,
    propTypes.element,
    propTypes.func
  ]),
  labelElement: propTypes.element,
  name: propTypes.string.isRequired,
  value: propTypes.oneOfType([
    propTypes.string,
    propTypes.number,
    propTypes.object
  ]).isRequired,
  onBlur: propTypes.func,
  onChange: propTypes.func,
  onKeyDown: propTypes.func,
  hideLabel: propTypes.bool,
  noLabel: propTypes.bool,
  fieldErrors: propTypes.array,
  hideErrors: propTypes.bool,
  placeholder: propTypes.string,
  options: propTypes.array.isRequired,
  isLoading: propTypes.bool,
  isClearable: propTypes.bool,
  showSearch: propTypes.bool,
  loadingMessage: propTypes.string,
  onOptionSelect: propTypes.func,
  onReset: propTypes.func,
  optionListFullWidth: propTypes.bool,
  compactIndicator: propTypes.bool,
  size: propTypes.oneOf([
    "auto",
    "full",
    "tiny",
    "sm",
    "md",
    "lg",
    "25p",
    "33p",
    "50p",
    "fill"
  ]),
  dataAttrib: propTypes.arrayOf(Object),
  dataWrapperAttrib: propTypes.arrayOf(Object),
  wrapperClassName: propTypes.string
};

SelectNonForm.defaultProps = {
  disabled: false,
  size: "md",
  optionListFullWidth: true,
  isClearable: false,
  showSearch: false,
  loadingMessage: "Loading..."
};

export default SelectNonForm;
